import { ArrowLeft, Pencil, Receipt, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsBc, useBonCommande } from '@/api/hooks';
import { LIBELLE_CATEGORIE_ACHAT, type DecisionApi } from '@/api/types';
import { BoutonPdf, Chargement, CircuitTimeline, ConfirmationDialog, ControlesListe, DecisionDialog, ErreurBloc, EtapeBadge, Historique, PageHeader, PaiementBadge, StatutBadge } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateHeureFr, montant } from '@/lib/utils';

export function BcDetail() {
  const { id } = useParams();
  const { data, isLoading, error } = useBonCommande(id);
  const actions = useActionsBc();
  const [dialogue, setDialogue] = useState<'decision' | 'soumettre' | 'resoumettre' | null>(null);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { bonCommande: bc, circuit, actions: a } = data;
  const erreur = (e: unknown) => {
    const c = controlesDe(e);
    toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
  };
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const decider = (decision: DecisionApi, motif?: string) => executer(() => actions.decider.mutateAsync({ id: bc.id, decision, motif }), decision === 'VALIDER' && a.etape === 'DIRECTEUR_GENERAL' ? 'Bon de commande autorisé.' : 'Décision enregistrée.');
  const retenu = bc.fournisseur.toLowerCase();

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/achats" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Bons de commande
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {bc.numero ?? 'Bon de commande (brouillon)'} <StatutBadge statut={bc.statut} /> {bc.statut === 'SOUMIS' && <EtapeBadge etape={data.etapeCourante} />}
            <Badge variant="outline">{LIBELLE_CATEGORIE_ACHAT[bc.categorie]}</Badge>
          </span>
        }
        sousTitre={
          <>
            {bc.objet} · {bc.fournisseur} · pour {bc.structure?.libelle} · émis par {bc.emetteur?.prenom} {bc.emetteur?.nom}
            {bc.requisition && !data.requisitionLiee ? ` · réquisition ${bc.requisition}` : ''}
            {data.requisitionLiee && (
              <>
                {' · réquisition '}
                <Link to={`/requisitions/${data.requisitionLiee.id}`} className="text-primary hover:underline">
                  {data.requisitionLiee.numero ?? '(sans numéro)'}
                </Link>
                {` (estimée à ${montant(data.requisitionLiee.montantEstime, true)})`}
              </>
            )}
            {bc.dateSoumission ? ` · soumis le ${dateHeureFr(bc.dateSoumission)}` : ''}
            {bc.dateAutorisation ? ` · autorisé le ${dateHeureFr(bc.dateAutorisation)}` : ''}
          </>
        }
        actions={
          <>
            <BoutonPdf chemin={`/pdf/bons-commande/${bc.id}`} nom={`${bc.numero ?? 'BC'}.pdf`} />
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/achats/${bc.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre
              </Button>
            )}
            {a.resoumettre && (
              <Button onClick={() => setDialogue('resoumettre')}>
                <Send className="size-4" /> Soumettre à nouveau
              </Button>
            )}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {a.facturer && (
              <Button variant="secondary" asChild>
                <Link to={`/fed/nouveau?bc=${bc.id}`}>
                  <Receipt className="size-4" /> Engager la facture (FED)
                </Link>
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-primary/5 p-4">
              <div className="text-xs uppercase text-muted-foreground">Montant</div>
              <div className="num text-xl font-semibold">{montant(bc.montantUsd, true)}</div>
              {bc.devise !== 'USD' && <div className="text-xs text-muted-foreground">{bc.montant.toLocaleString('fr-FR')} {bc.devise}</div>}
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Ligne budgétaire</div>
              <div className="font-semibold">{bc.ligneBudgetaire?.code}</div>
              <div className="text-xs text-muted-foreground">{bc.ligneBudgetaire?.libelle}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Livraison</div>
              <div className="text-sm font-medium">{bc.delaiLivraison ?? '—'}</div>
              <div className="text-xs text-muted-foreground">{bc.lieuLivraison ?? ''}</div>
            </div>
          </div>

          {data.budget && (
            <Alert variant={data.budget.ok ? 'default' : 'destructive'}>
              <AlertTitle>{data.budget.ok ? 'Disponibilité budgétaire confirmée' : 'Dépassement budgétaire'}</AlertTitle>
              <AlertDescription>
                {data.budget.message} Disponible {montant(data.budget.disponible)} · prévisionnel {montant(data.budget.disponiblePrevisionnel)}.
              </AlertDescription>
            </Alert>
          )}

          {data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles avant soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesListe controles={data.controles} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Lignes de la commande</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Désignation</TableHead>
                    <TableHead className="text-right">Quantité</TableHead>
                    <TableHead className="text-right">Prix unitaire</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bc.lignes.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.designation}</TableCell>
                      <TableCell className="num text-right">
                        {l.quantite} {l.unite}
                      </TableCell>
                      <TableCell className="num text-right">{montant(l.prixUnitaire, true)}</TableCell>
                      <TableCell className="num text-right">{montant(l.quantite * l.prixUnitaire, true)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} className="text-right font-medium">
                      Total ({bc.devise})
                    </TableCell>
                    <TableCell className="num text-right font-semibold">{montant(bc.montant, true)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Offres consultées</CardTitle>
              <CardDescription>{data.moinsDisant ? `Moins-disant : ${data.moinsDisant.fournisseur} à ${montant(data.moinsDisant.montant, true)}` : 'Aucune offre consultée.'}</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {bc.consultations.map((c, i) => (
                    <TableRow key={i} className={cn(c.fournisseur.toLowerCase() === retenu && 'bg-primary/5')}>
                      <TableCell>
                        {c.fournisseur}
                        {c.fournisseur.toLowerCase() === retenu && <Badge className="ml-2">retenu</Badge>}
                        {data.moinsDisant?.fournisseur === c.fournisseur && (
                          <Badge variant="outline" className="ml-2">
                            moins-disant
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="num text-right">{montant(c.montant, true)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {bc.justification && (
                <div className="mt-3 text-sm">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Justification du choix</div>
                  <p>{bc.justification}</p>
                </div>
              )}
            </CardContent>
          </Card>
          <PiecesJointes objetType="BC" objetId={bc.id} typesSuggeres={['PROFORMA', 'CONTRAT', 'JUSTIFICATIF', 'AUTRE']} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {circuit && <CardDescription>Achats → Cellule des marchés → Directeur Général</CardDescription>}
            </CardHeader>
            <CardContent>
              <CircuitTimeline circuit={circuit} />
            </CardContent>
          </Card>
          {data.feds.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Factures engagées</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.feds.map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/fed/${f.id}`} className="font-medium text-primary hover:underline">
                      {f.numero ?? 'FED (brouillon)'}
                    </Link>
                    <span className="flex items-center gap-1">
                      <StatutBadge statut={f.statut} />
                      {f.statut === 'AUTORISE' && <PaiementBadge etat={f.etatPaiement} />}
                    </span>
                    <span className="num">{montant(f.montantUsd, true)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {circuit && circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={circuit} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DecisionDialog ouvert={dialogue === 'decision'} onOpenChange={(o) => setDialogue(o ? 'decision' : null)} decisions={a.decisions} etape={a.etape} onConfirmer={decider} chargement={actions.decider.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'soumettre'} onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)} titre="Soumettre le bon de commande" description="Un numéro sera attribué et le circuit ouvert : responsable des achats, Cellule des marchés, Directeur Général." libelle="Soumettre" onConfirmer={() => executer(() => actions.soumettre.mutateAsync(bc.id), 'Bon de commande soumis.')} chargement={actions.soumettre.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'resoumettre'} onOpenChange={(o) => setDialogue(o ? 'resoumettre' : null)} titre="Soumettre à nouveau" libelle="Soumettre à nouveau" onConfirmer={(r) => executer(() => actions.resoumettre.mutateAsync({ id: bc.id, reponse: r || undefined }), 'Bon de commande soumis à nouveau.')} chargement={actions.resoumettre.isPending} champ={{ label: 'Réponse au validateur' }} />
    </div>
  );
}
